import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Badge,
  Modal,
  Form,
  Alert,
  Spinner,
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBook,
  FaQuestionCircle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import TestService from '../../../services/testService';

const QuestionCategories = ({ onSelectCategory, selectedCategoryId }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await TestService.getCategories();
      console.log("Categories response:", response);
      // Ensure we always extract the array correctly whether it's wrapped in data or not
      const categoryData = Array.isArray(response) ? response : (response.data || []);
      setCategories(categoryData);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        await TestService.updateCategory(editingCategory.id, {
          name: categoryName,
          description: categoryDescription,
        });
        toast.success('Category updated successfully');
      } else {
        await TestService.createCategory({
          name: categoryName,
          description: categoryDescription,
        });
        toast.success('Category created successfully');
      }
      setShowModal(false);
      resetForm();
      loadCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;

    try {
      await TestService.deleteCategory(deletingCategory.id);
      toast.success('Category deleted successfully');
      setShowDeleteModal(false);
      setDeletingCategory(null);
      loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryDescription('');
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryDescription(category.description || '');
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <FaBook className="me-2 text-primary" />
          Question Categories
        </h5>
        <Button size="sm" onClick={() => setShowModal(true)}>
          <FaPlus className="me-1" /> New Category
        </Button>
      </div>

      {categories.length === 0 ? (
        <Alert variant="info" className="text-center">
          No categories created yet. Create a category to organize your questions.
        </Alert>
      ) : (
        <div className="row">
          {categories.map((category) => (
            <div key={category.id} className="col-md-6 col-lg-4 mb-3">
              <Card
                className={`h-100 cursor-pointer ${
                  selectedCategoryId === category.id ? 'border-primary shadow' : ''
                }`}
                style={{ cursor: 'pointer' }}
                onClick={() => onSelectCategory && onSelectCategory(category.id)}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">{category.name}</h6>
                      <small className="text-muted">
                        {category.description || 'No description'}
                      </small>
                    </div>
                    <Badge bg="info" pill>
                      <FaQuestionCircle className="me-1" size={10} />
                      {category.total_questions || 0} Questions
                    </Badge>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-transparent d-flex justify-content-end gap-2">
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(category);
                    }}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingCategory(category);
                      setShowDeleteModal(true);
                    }}
                  >
                    <FaTrash />
                  </Button>
                </Card.Footer>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? 'Edit Category' : 'Create New Category'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., JavaScript, Python, HTML/CSS"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Brief description of this category"
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCategory}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Delete Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deletingCategory && (
            <>
              <p>Are you sure you want to delete the category:</p>
              <p className="fw-bold text-danger">{deletingCategory.name}</p>
              <Alert variant="warning">
                This will also delete all questions in this category.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCategory}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuestionCategories;